import { useAtomValue, useSetAtom } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import BPAFindingSummaryCard from './BPAFindingSummaryCard';
import { recheckBPAFinding } from '../../services/bpaFindingsService';
import { findingsDataRefreshAtom } from '../../state';
import { BPAFinding } from '../../types/bpaFindings.types';

interface BPAFindingRecheckModalProps {
  finding: BPAFinding;
  isOpen: boolean;
  toggle: () => void;
}

const BPAFindingRecheckModal: React.FC<BPAFindingRecheckModalProps> = ({
  finding,
  isOpen,
  toggle,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const refreshFindingsGridData = useSetAtom(findingsDataRefreshAtom);
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(false);

  const loadingButtonContent = (
    <>
      <span
        className="spinner-border spinner-border-sm mr-2"
        role="status"
        aria-hidden="true"
      />
      Re-evaluating...
    </>
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const handleSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> => {
    event.preventDefault();

    if (!companyId || companyId === -1) {
      showFailureToast(
        'Please select a company before re-evaluating findings.',
      );
      return;
    }

    setLoading(true);

    let shouldClose = false;

    try {
      const response = await recheckBPAFinding(finding.id, {
        company_id: companyId,
      });

      if (!isMountedRef.current) return;

      showSuccessToast(
        response.message || 'Re-evaluate finding scan initiated successfully.',
      );
      refreshFindingsGridData(new Date().toISOString());
      shouldClose = true;
    } catch (err) {
      if (!isMountedRef.current) return;

      showFailureToast(
        extractApiErrorMessage(err, 'Failed to re-evaluate BPA finding.'),
      );
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        if (shouldClose) {
          toggle();
        }
      }
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={loading ? undefined : toggle} size="md">
      <ModalHeader toggle={loading ? undefined : toggle}>
        Re-evaluate Finding
      </ModalHeader>
      <ModalBody className="pb-2">
        <BPAFindingSummaryCard rowData={finding} />
        <p className="mt-3 mb-0">
          Queue a re-evaluation scan for this finding.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          className="text-nowrap"
          style={{
            alignItems: 'center',
            display: 'inline-flex',
            justifyContent: 'center',
            minWidth: '148px',
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? loadingButtonContent : 'Re-evaluate Finding'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BPAFindingRecheckModal;
